import { useCallback, useMemo } from 'react'

import {
  DSAlert,
  DSButton,
  DSCircularLoading,
  DSDropdown,
  DSPagination,
  DSTag,
  Table as DSTable,
  TableAlign
} from '@vr/ds-react'

import { TableActionsDropdown } from '../table-actions-dropdown'
import { REPORT_LIST_COLUMNS } from '@/constants/report-list-columns'
import { STATUS_REPORT } from '@/constants/status-report'
import { useReportContext } from '@/contexts/report.context'
import { formatDateToBR } from '@/utils/format-date-to-br'

export const ReportList = () => {
  const {
    tableData,
    isLoading,
    handleLimitChange: onLimitChange,
    limit,
    results,
    setPage,
    handleShowReports
  } = useReportContext()

  const mappedData = useMemo(() => {
    return tableData.map(item => {
      return {
        ...item
      }
    })
  }, [tableData])

  const handleLimitChange = useCallback(
    (selectedOptions: string | number | (string | number)[]) => {
      let value: string | number | undefined
      if (Array.isArray(selectedOptions)) {
        value = selectedOptions[0]
      } else {
        value = selectedOptions
      }
      const newLimit =
        typeof value === 'number' ? value : Number.parseInt(value, 10)
      onLimitChange(newLimit)
    },
    [onLimitChange]
  )

  return (
    <>
      {isLoading && <DSCircularLoading isLoading shouldBlur={true} />}

      {!isLoading && mappedData.length === 0 && (
        <DSAlert
          type='info'
          title={'Sem resultados para os filtros selecionados'}
          css={{ marginTop: '$sm16' }}
          hiddenBtnClose
        >
          Tente ajustar os filtros para exibir os resultados desejados
        </DSAlert>
      )}

      {mappedData.length > 0 && (
        <>
          <DSTable css={{ width: '100%', marginTop: '20px' }}>
            <DSTable.Head>
              <DSTable.Cell colSpan={5} align='right'>
                <>
                  <DSButton
                    variant='ghost'
                    size='small'
                    outlined
                    css={{ marginRight: '$sm16' }}
                    onClick={handleShowReports}
                    iconLeft='principal-refresh'
                  >
                    Atualizar
                  </DSButton>
                  <DSDropdown
                    options={[
                      { label: '10' },
                      { label: '25' },
                      { label: '50' },
                      { label: '100' }
                    ]}
                    onSelectionChange={handleLimitChange}
                    css={{ padding: '16px 0' }}
                  >
                    <DSButton
                      variant='secondary'
                      size='small'
                      iconRight='principal-chevron-down'
                    >
                      {`Mostrar: ${limit}`}
                    </DSButton>
                  </DSDropdown>
                </>
              </DSTable.Cell>
            </DSTable.Head>
            <DSTable.Head>
              {REPORT_LIST_COLUMNS.map(item => (
                <DSTable.Cell
                  key={item.field}
                  id={item.field}
                  align={item.align as TableAlign}
                >
                  {item.headerName}
                </DSTable.Cell>
              ))}
            </DSTable.Head>
            <DSTable.Body>
              {mappedData.map(item => (
                <DSTable.Row key={item.id} id={item.id?.toString()}>
                  {REPORT_LIST_COLUMNS.map(key => {
                    let cellContent: any
                    switch (key.field) {
                      case 'acoes':
                        cellContent = item.linkParaBaixar && (
                          <TableActionsDropdown
                            pageResource='report'
                            onClick={() => {
                              window.open(item.linkParaBaixar, '_blank')
                            }}
                          />
                        )
                        break
                      case 'dataDaSolicitacao':
                        cellContent = formatDateToBR(
                          new Date(item.dataDaSolicitacao)
                        )
                        break
                      case 'status': {
                        const variant = STATUS_REPORT[item.status] || {
                          label: item.status,
                          style: 'outlined'
                        }
                        cellContent = (
                          <DSTag
                            variant={variant.style}
                            css={{ margin: '0 auto' }}
                          >
                            {variant.label}
                          </DSTag>
                        )
                        break
                      }
                      default:
                        cellContent = item[key.field as keyof typeof item]
                    }

                    return (
                      <DSTable.Cell
                        key={key.field}
                        align={key.align as TableAlign}
                        css={{
                          padding: 0,
                          overflowWrap: 'break-word',
                          whiteSpace: 'normal'
                        }}
                      >
                        {cellContent}
                      </DSTable.Cell>
                    )
                  })}
                </DSTable.Row>
              ))}
            </DSTable.Body>
          </DSTable>

          <DSPagination
            total={results}
            maxPerPage={limit}
            handleChange={(newPage: number) => setPage(newPage)}
            nextPreview
            siblingCount={1}
          />
        </>
      )}
    </>
  )
}
